import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { BaseAsset, Language, Theme } from '@/shared/types';
import {
  APP_SETTINGS_KEY,
  DEFAULT_BASE_ASSET,
  FALLBACK_LANGUAGE,
} from '@/shared/config';
import { v4 as uuidv4 } from 'uuid';

interface AppSettingsState {
  // Theme
  theme: Theme | null;
  setTheme: (theme: Theme) => void;

  // Language
  language: Language | null;
  getLanguage: () => Language;
  setLanguage: (language: Language) => void;

  // Base Asset
  baseAsset: BaseAsset;
  setBaseAsset: (baseAsset: BaseAsset) => void;

  // Device ID
  deviceId: string;

  twoFAShown: Record<string, boolean>;
  isTwoFAShown: (userId: string) => boolean;
  setTwoFAShown: (userId: string, shown: boolean) => void;
  previousNavigateUrl: string;
  setPreviousNavigateUrl: (url: string) => void;

  isBalancesHidden: boolean;
  setIsBalancesHidden: () => void;

  isLowBalancesHidden: boolean;
  setIsLowBalancesHidden: () => void;
}

export const useAppSettingsStore = create<AppSettingsState>()(
  persist(
    (set, get) => ({
      theme: 'light',
      setTheme: theme => {
        //mvp disabling dark theme from: user prefferences; localstorage.
        set({ theme: theme === 'dark' ? 'light' : theme });
      },

      language: null,
      setLanguage: language => {
        set({ language });
      },
      getLanguage: () => {
        return get().language || FALLBACK_LANGUAGE;
      },

      baseAsset: DEFAULT_BASE_ASSET,
      setBaseAsset: baseAsset => {
        set({ baseAsset });
      },

      deviceId: get()?.deviceId || uuidv4(),

      twoFAShown: {},
      isTwoFAShown: (userId: string) => {
        return get().twoFAShown[userId] ?? false;
      },
      setTwoFAShown: (userId: string, shown: boolean) => {
        set(state => ({
          twoFAShown: {
            ...state.twoFAShown,
            [userId]: shown,
          },
        }));
      },

      previousNavigateUrl: '',
      setPreviousNavigateUrl: (url: string) => {
        set(() => ({
          previousNavigateUrl: url,
        }));
      },

      isBalancesHidden: false,
      setIsBalancesHidden: () => {
        set(state => ({
          isBalancesHidden: !state.isBalancesHidden,
        }));
      },

      isLowBalancesHidden: false,
      setIsLowBalancesHidden: () => {
        set(state => ({
          isLowBalancesHidden: !state.isLowBalancesHidden,
        }));
      },
    }),
    {
      name: APP_SETTINGS_KEY,
    }
  )
);
